import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, ArrowUpRight, Briefcase, Building2, CalendarClock, Package, RefreshCw, Search, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_META = {
  prospecto: { label: 'Prospecto', badge: 'border-border-subtle text-text-muted bg-card/40' },
  onboarding: { label: 'Onboarding', badge: 'border-amber-400/20 text-amber-200 bg-amber-500/10' },
  activo: { label: 'Activo', badge: 'border-primary/20 text-primary bg-primary/10' },
  escalando: { label: 'Escalando', badge: 'border-emerald-400/20 text-emerald-200 bg-emerald-500/10' },
  pausado: { label: 'Pausado', badge: 'border-rose-400/20 text-rose-200 bg-rose-500/10' },
};

const PROCESS_META = {
  pendiente_de_activar: { label: 'Pendiente de activar', badge: 'border-amber-400/20 text-amber-200 bg-amber-500/10' },
  agentes_trabajando: { label: 'Agentes trabajando', badge: 'border-sky-400/20 text-sky-200 bg-sky-500/10' },
  redactando_informe: { label: 'Redactando informe', badge: 'border-indigo-400/20 text-indigo-200 bg-indigo-500/10' },
  ordenes_ejecutivas_abiertas: { label: 'Órdenes ejecutivas abiertas', badge: 'border-fuchsia-400/20 text-fuchsia-200 bg-fuchsia-500/10' },
};

const DEPARTMENT_STATUS = {
  activo: 'border-primary/20 bg-primary/10 text-primary',
  observando: 'border-amber-400/20 bg-amber-500/10 text-amber-200',
  en_espera: 'border-border-subtle bg-card/40 text-text-muted',
};

const BILLING_STATUS_META = {
  pending_link_or_manual: 'border-amber-400/20 bg-amber-500/10 text-amber-200',
  awaiting_delivery: 'border-sky-400/20 bg-sky-500/10 text-sky-200',
  ready_to_charge: 'border-primary/20 bg-primary/10 text-primary',
  paid: 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200',
  active: 'border-primary/20 bg-primary/10 text-primary',
  scheduled_after_delivery: 'border-amber-400/20 bg-amber-500/10 text-amber-200',
  failed: 'border-rose-400/20 bg-rose-500/10 text-rose-200',
};

const normalizeProcessKey = (value = '') => value.toLowerCase().replaceAll(' ', '_');
const slugify = (value = '') => value.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();

const MetricCard = ({ icon: Icon, label, value, helper, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
        <p className="mt-3 text-3xl font-bold text-text-heading" data-testid={`${testId}-value`}>{value}</p>
        <p className="mt-2 text-xs text-text-muted" data-testid={`${testId}-helper`}>{helper}</p>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary" data-testid={`${testId}-icon`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

const StageColumn = ({ stageKey, clients }) => {
  const meta = STAGE_META[stageKey] || { label: stageKey, badge: 'border-border-subtle text-text-muted bg-card/40' };

  return (
    <div className="min-w-[280px] rounded-[28px] border border-border-subtle bg-card/30 p-4" data-testid={`client-operations-stage-column-${stageKey}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Fase</p>
          <h4 className="mt-2 text-lg font-semibold text-text-heading" data-testid={`client-operations-stage-title-${stageKey}`}>{meta.label}</h4>
        </div>
        <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${meta.badge}`} data-testid={`client-operations-stage-count-${stageKey}`}>
          {clients.length}
        </span>
      </div>

      <div className="mt-4 space-y-3">
        {clients.length ? clients.map((client) => (
          <div key={client.id} className="rounded-[22px] border border-border-subtle bg-background/60 p-4" data-testid={`client-operations-stage-card-${client.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-text-heading" data-testid={`client-operations-stage-card-name-${client.id}`}>{client.company_name}</p>
                <p className="mt-1 text-xs text-text-muted" data-testid={`client-operations-stage-card-process-${client.id}`}>{client.process_label}</p>
              </div>
              <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary" data-testid={`client-operations-stage-card-tasks-${client.id}`}>
                {client.active_tasks_count || 0} activos
              </span>
            </div>

            {!!client.services_active?.length && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid={`client-operations-stage-card-services-${client.id}`}>
                {client.services_active.slice(0, 3).map((service) => (
                  <span key={`${client.id}-${service}`} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted" data-testid={`client-operations-stage-card-service-${client.id}-${slugify(service)}`}>
                    {service}
                  </span>
                ))}
              </div>
            )}
          </div>
        )) : <p className="rounded-[20px] border border-dashed border-border-subtle bg-background/35 p-4 text-sm text-text-muted" data-testid={`client-operations-stage-empty-${stageKey}`}>Sin clientes en esta fase.</p>}
      </div>
    </div>
  );
};

export const ClientOperationsHub = ({ authFetch }) => {
  const [hub, setHub] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');

  const loadHub = useCallback(async (force = false) => {
    try {
      setRefreshing(force);
      const res = await authFetch(`${API}/admin/client-operations`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el panel de clientes');
      setHub(data);
      if (force) toast.success('Panel de clientes actualizado');
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar el panel de clientes');
    } finally {
      setRefreshing(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadHub(false);
  }, [loadHub]);

  const filteredClients = useMemo(() => {
    if (!hub?.clients) return [];
    const query = search.trim().toLowerCase();

    return hub.clients.filter((client) => {
      const matchesStage = stageFilter === 'all' || client.stage === stageFilter;
      const haystack = [
        client.company_name,
        client.contact_name,
        client.contact_email,
        client.primary_goal,
        ...(client.services_active || []),
        ...(client.active_agents || []),
      ].filter(Boolean).join(' ').toLowerCase();
      const matchesSearch = !query || haystack.includes(query);
      return matchesStage && matchesSearch;
    });
  }, [hub, search, stageFilter]);

  const groupedByStage = useMemo(() => {
    const base = { prospecto: [], onboarding: [], activo: [], escalando: [], pausado: [] };
    filteredClients.forEach((client) => {
      const key = base[client.stage] ? client.stage : 'prospecto';
      base[key].push(client);
    });
    return base;
  }, [filteredClients]);

  if (!hub) return <div className="glass-card p-8 text-center text-text-muted" data-testid="client-operations-loading">Cargando panel de clientes...</div>;

  return (
    <div className="space-y-6" data-testid="client-operations-hub">
      <div className="glass-card tracing-panel p-6 xl:p-8" data-testid="client-operations-line-identity-card">
        <p className="editorial-kicker mb-4">Neuralia Solutions</p>
        <h3 className="text-3xl font-semibold text-text-heading">Clientes finales, entregas y operación consultiva</h3>
        <p className="mt-4 max-w-4xl text-base text-text-body">Este hub representa la línea <strong>Solutions</strong>: empresas y emprendedores que contratan servicios IA. La línea <strong>Partner</strong> queda separada en su propio Office para red comercial, comisiones y afiliación inteligente.</p>
      </div>
      <div className="glass-card tracing-panel overflow-hidden" data-testid="client-operations-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-5" data-testid="client-operations-kicker">Panel de Clientes</p>
            <h3 className="text-3xl font-semibold text-text-heading" data-testid="client-operations-heading">Vista mixta de kanban y operaciones para saber qué cliente está en qué momento</h3>
            <p className="mt-4 max-w-3xl text-base text-text-body" data-testid="client-operations-subheading">Aquí se cruza etapa, servicios vivos y departamentos IA trabajando para que el equipo vea el estado real de la cartera sin saltar entre briefings, órdenes y tareas.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard icon={Building2} label="Clientes totales" value={hub.metrics.total_clients} helper="base detectada en todo el sistema" testId="client-operations-metric-total" />
            <MetricCard icon={Users} label="Clientes activos" value={hub.metrics.active_clients} helper="con operación, onboarding o servicios vivos" testId="client-operations-metric-active" />
            <MetricCard icon={Briefcase} label="Servicios activos" value={hub.metrics.services_live} helper="suma de servicios en curso" testId="client-operations-metric-services" />
            <MetricCard icon={Sparkles} label="Agentes trabajando" value={hub.metrics.agents_working} helper="agentes distintos visibles ahora" testId="client-operations-metric-agents" />
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.78fr_1.22fr]">
        <div className="space-y-6">
          <div className="glass-card p-6" data-testid="client-operations-status-card">
            <div className="flex items-center gap-2 text-text-heading">
              <Activity className="h-5 w-5 text-primary" />
              <h4 className="text-lg font-semibold" data-testid="client-operations-status-title">Radar operativo</h4>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                ['onboarding', 'Onboarding', hub.status_metrics.onboarding],
                ['execution', 'En ejecución', hub.status_metrics.execution],
                ['scheduled', 'Con agenda', hub.status_metrics.scheduled],
                ['blocked', 'Bloqueados', hub.status_metrics.blocked],
              ].map(([key, label, value]) => (
                <div key={key} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`client-operations-status-${key}`}>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
                  <p className="mt-2 text-2xl font-bold text-text-heading" data-testid={`client-operations-status-value-${key}`}>{value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-6" data-testid="client-operations-upcoming-card">
            <div className="flex items-center gap-2 text-text-heading">
              <CalendarClock className="h-5 w-5 text-primary" />
              <h4 className="text-lg font-semibold" data-testid="client-operations-upcoming-title">Próximas citas y agenda</h4>
            </div>
            <div className="mt-4 space-y-3">
              {hub.upcoming_agenda.length ? hub.upcoming_agenda.map((item, index) => (
                <div key={`${item.client_id}-${item.fecha}-${item.hora}`} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`client-operations-upcoming-item-${index}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text-heading" data-testid={`client-operations-upcoming-company-${index}`}>{item.company_name}</p>
                      <p className="mt-1 text-xs text-text-muted" data-testid={`client-operations-upcoming-type-${index}`}>{item.tipo || 'Sesión programada'}</p>
                    </div>
                    <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary" data-testid={`client-operations-upcoming-date-${index}`}>{item.fecha} · {item.hora}</span>
                  </div>
                  {item.consultant_name && <p className="mt-2 text-xs text-text-muted" data-testid={`client-operations-upcoming-consultant-${index}`}>Consultor: {item.consultant_name}</p>}
                </div>
              )) : <p className="rounded-[20px] border border-dashed border-border-subtle bg-background/35 p-4 text-sm text-text-muted" data-testid="client-operations-upcoming-empty">No hay próximas citas detectadas.</p>}
            </div>
          </div>

          <div className="glass-card p-6" data-testid="client-operations-financial-ops-card">
            <div className="flex items-center gap-2 text-text-heading">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h4 className="text-lg font-semibold" data-testid="client-operations-financial-title">Agente financiero / contable</h4>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="client-operations-financial-contracts">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Contratos</p>
                <p className="mt-2 text-2xl font-bold text-text-heading">{hub.billing_overview?.contracts || 0}</p>
              </div>
              <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="client-operations-financial-balances-ready">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">50% listos</p>
                <p className="mt-2 text-2xl font-bold text-text-heading">{hub.billing_overview?.balance_ready_to_charge || 0}</p>
              </div>
              <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="client-operations-financial-documents">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Documentos</p>
                <p className="mt-2 text-2xl font-bold text-text-heading">{hub.financial_ops?.documents || 0}</p>
              </div>
              <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="client-operations-financial-stock">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Stock</p>
                <p className="mt-2 text-2xl font-bold text-text-heading">{hub.financial_ops?.stock_items || 0}</p>
              </div>
              <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="client-operations-financial-alerts">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Alertas stock</p>
                <p className="mt-2 text-2xl font-bold text-text-heading">{hub.financial_ops?.low_stock_alerts || 0}</p>
              </div>
              <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid="client-operations-financial-purchases">
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Compras pendientes</p>
                <p className="mt-2 text-2xl font-bold text-text-heading">{hub.financial_ops?.pending_purchase_approvals || 0}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card p-6" data-testid="client-operations-filters-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted" data-testid="client-operations-filters-kicker">Lista operativa</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading" data-testid="client-operations-filters-title">Busca por empresa, servicio o agente asignado</h4>
            </div>
            <button onClick={() => loadHub(true)} className="btn-secondary" disabled={refreshing} data-testid="client-operations-refresh-button">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Actualizando...' : 'Refrescar'}
            </button>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-[1fr_auto]">
            <label className="relative block" data-testid="client-operations-search-wrapper">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Ej. Clínica, ventas, VENTAS"
                className="input-kinesthetic pl-11"
                data-testid="client-operations-search-input"
              />
            </label>

            <select value={stageFilter} onChange={(event) => setStageFilter(event.target.value)} className="input-kinesthetic min-w-[180px]" data-testid="client-operations-stage-filter">
              <option value="all">Todas las fases</option>
              {Object.entries(STAGE_META).map(([key, meta]) => <option key={key} value={key}>{meta.label}</option>)}
            </select>
          </div>

          <div className="mt-5 flex flex-wrap gap-2" data-testid="client-operations-stage-breakdown">
            {Object.entries(STAGE_META).map(([key, meta]) => {
              const count = hub.stage_breakdown.find((item) => item.stage === key)?.count || 0;
              return (
                <span key={key} className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${meta.badge}`} data-testid={`client-operations-stage-chip-${key}`}>
                  {meta.label} · {count}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="glass-card p-6" data-testid="client-operations-kanban-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted" data-testid="client-operations-kanban-kicker">Vista kanban</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading" data-testid="client-operations-kanban-title">Pipeline visual por fase de cliente</h4>
          </div>
          <span className="rounded-full border border-border-subtle px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted" data-testid="client-operations-kanban-total-filtered">{filteredClients.length} visibles</span>
        </div>

        <div className="mt-5 flex gap-4 overflow-x-auto pb-2" data-testid="client-operations-kanban-columns">
          {Object.keys(STAGE_META).map((stageKey) => <StageColumn key={stageKey} stageKey={stageKey} clients={groupedByStage[stageKey] || []} />)}
        </div>
      </div>

      <div className="space-y-4" data-testid="client-operations-list-card">
        {filteredClients.length ? filteredClients.map((client) => {
          const stageMeta = STAGE_META[client.stage] || STAGE_META.prospecto;
          const processKey = normalizeProcessKey(client.process_label || '');
          const processMeta = PROCESS_META[processKey] || { label: client.process_label || 'En seguimiento', badge: 'border-border-subtle text-text-muted bg-card/40' };

          return (
            <div key={client.id} className="glass-card tracing-panel p-6" data-testid={`client-operations-client-card-${client.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-3xl">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${stageMeta.badge}`} data-testid={`client-operations-client-stage-${client.id}`}>{stageMeta.label}</span>
                    <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${processMeta.badge}`} data-testid={`client-operations-client-process-${client.id}`}>{processMeta.label}</span>
                  </div>
                  <h4 className="mt-3 text-2xl font-semibold text-text-heading" data-testid={`client-operations-client-name-${client.id}`}>{client.company_name}</h4>
                  <p className="mt-2 text-sm text-text-body" data-testid={`client-operations-client-goal-${client.id}`}>{client.primary_goal || 'Sin objetivo principal documentado todavía.'}</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-text-muted" data-testid={`client-operations-client-meta-${client.id}`}>
                    {client.contact_name && <span data-testid={`client-operations-client-contact-${client.id}`}>{client.contact_name}</span>}
                    {client.contact_email && <span data-testid={`client-operations-client-email-${client.id}`}>{client.contact_email}</span>}
                    {client.industry && <span data-testid={`client-operations-client-industry-${client.id}`}>{client.industry}</span>}
                    {client.latest_activity_at && <span data-testid={`client-operations-client-activity-${client.id}`}>Actividad: {new Date(client.latest_activity_at).toLocaleString('es-ES')}</span>}
                  </div>
                </div>

                <div className="grid min-w-[260px] gap-3 sm:grid-cols-3" data-testid={`client-operations-client-counters-${client.id}`}>
                  <div className="rounded-[18px] border border-border-subtle bg-background/55 p-3" data-testid={`client-operations-client-counter-tasks-${client.id}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Tareas</p>
                    <p className="mt-2 text-xl font-semibold text-text-heading">{client.active_tasks_count || 0}</p>
                  </div>
                  <div className="rounded-[18px] border border-border-subtle bg-background/55 p-3" data-testid={`client-operations-client-counter-orders-${client.id}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Órdenes</p>
                    <p className="mt-2 text-xl font-semibold text-text-heading">{client.open_orders_count || 0}</p>
                  </div>
                  <div className="rounded-[18px] border border-border-subtle bg-background/55 p-3" data-testid={`client-operations-client-counter-agents-${client.id}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Agentes</p>
                    <p className="mt-2 text-xl font-semibold text-text-heading">{client.active_agents?.length || 0}</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
                <div className="space-y-4">
                  <div className="rounded-[24px] border border-border-subtle bg-card/40 p-4" data-testid={`client-operations-client-services-panel-${client.id}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Servicios activos</p>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid={`client-operations-client-services-${client.id}`}>
                      {client.services_active?.length ? client.services_active.map((service) => (
                        <span key={`${client.id}-${service}`} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary" data-testid={`client-operations-client-service-${client.id}-${slugify(service)}`}>
                          {service}
                        </span>
                      )) : <span className="text-sm text-text-muted" data-testid={`client-operations-client-services-empty-${client.id}`}>Sin servicios visibles todavía.</span>}
                    </div>
                  </div>

                  <div className="rounded-[24px] border border-border-subtle bg-card/40 p-4" data-testid={`client-operations-client-agents-panel-${client.id}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Agentes asignados</p>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid={`client-operations-client-agents-${client.id}`}>
                      {client.active_agents?.length ? client.active_agents.map((agent) => (
                        <span key={`${client.id}-${agent}`} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted" data-testid={`client-operations-client-agent-${client.id}-${agent.toLowerCase()}`}>
                          {agent}
                        </span>
                      )) : <span className="text-sm text-text-muted" data-testid={`client-operations-client-agents-empty-${client.id}`}>Sin agentes activos detectados.</span>}
                    </div>
                  </div>

                  <div className="rounded-[24px] border border-border-subtle bg-card/40 p-4" data-testid={`client-operations-client-next-step-panel-${client.id}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Próximo hito</p>
                    <p className="mt-3 text-sm text-text-body" data-testid={`client-operations-client-next-step-${client.id}`}>{client.next_milestone}</p>
                    {client.next_booking && <p className="mt-2 text-xs text-text-muted" data-testid={`client-operations-client-next-booking-${client.id}`}>Cita: {client.next_booking.fecha} · {client.next_booking.hora} · {client.next_booking.tipo_nombre || client.next_booking.tipo || 'sesión'}</p>}
                  </div>

                  <div className="rounded-[24px] border border-border-subtle bg-card/40 p-4" data-testid={`client-operations-client-billing-panel-${client.id}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Cobros y mantenimiento</p>
                    {client.billing_snapshot?.project_total_amount ? (
                      <div className="mt-3 space-y-2 text-sm text-text-body">
                        <p>Total proyecto: <span className="text-text-heading">${Number(client.billing_snapshot.project_total_amount || 0).toLocaleString('en-US')}</span></p>
                        <div className="flex flex-wrap gap-2" data-testid={`client-operations-client-billing-statuses-${client.id}`}>
                          <span className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${BILLING_STATUS_META[client.billing_snapshot.deposit_status] || 'border-border-subtle bg-background/55 text-text-muted'}`}>depósito {client.billing_snapshot.deposit_status}</span>
                          <span className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${BILLING_STATUS_META[client.billing_snapshot.balance_status] || 'border-border-subtle bg-background/55 text-text-muted'}`}>saldo {client.billing_snapshot.balance_status}</span>
                          <span className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${BILLING_STATUS_META[client.billing_snapshot.maintenance_status] || 'border-border-subtle bg-background/55 text-text-muted'}`}>mantenimiento {client.billing_snapshot.maintenance_status}</span>
                        </div>
                        <p>Ciclos: <span className="text-text-heading">{client.billing_snapshot.maintenance_cycles_charged || 0}/{client.billing_snapshot.maintenance_cycles_total || 0}</span></p>
                      </div>
                    ) : <p className="mt-3 text-sm text-text-muted">Aún no hay contrato de cobro vinculado a este cliente.</p>}
                  </div>
                </div>

                <div className="rounded-[24px] border border-border-subtle bg-card/40 p-4" data-testid={`client-operations-client-departments-panel-${client.id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Departamentos trabajando</p>
                      <h5 className="mt-2 text-lg font-semibold text-text-heading" data-testid={`client-operations-client-departments-title-${client.id}`}>Mapa de agentes por área</h5>
                    </div>
                    {!!client.detail_urls?.length && (
                      <a href={client.detail_urls[0]} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-primary" data-testid={`client-operations-client-primary-link-${client.id}`}>
                        Abrir referencia <ArrowUpRight className="h-4 w-4" />
                      </a>
                    )}
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {client.departments?.length ? client.departments.map((department) => (
                      <div key={`${client.id}-${department.id}`} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`client-operations-client-department-${client.id}-${department.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-text-heading" data-testid={`client-operations-client-department-name-${client.id}-${department.id}`}>{department.name}</p>
                            <p className="mt-1 text-xs text-text-muted" data-testid={`client-operations-client-department-helper-${client.id}-${department.id}`}>{department.helper}</p>
                          </div>
                          <span className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${DEPARTMENT_STATUS[department.status] || DEPARTMENT_STATUS.en_espera}`} data-testid={`client-operations-client-department-status-${client.id}-${department.id}`}>
                            {department.status}
                          </span>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2" data-testid={`client-operations-client-department-agents-${client.id}-${department.id}`}>
                          {department.active_agents?.length ? department.active_agents.map((agent) => (
                            <span key={`${client.id}-${department.id}-${agent}`} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary" data-testid={`client-operations-client-department-agent-${client.id}-${department.id}-${agent.toLowerCase()}`}>
                              {agent}
                            </span>
                          )) : <span className="text-xs text-text-muted" data-testid={`client-operations-client-department-empty-${client.id}-${department.id}`}>Sin agente visible ahora.</span>}
                        </div>
                      </div>
                    )) : <div className="rounded-[20px] border border-dashed border-border-subtle bg-background/35 p-4 text-sm text-text-muted" data-testid={`client-operations-client-departments-empty-${client.id}`}>Todavía no hay departamentos detectados.</div>}
                  </div>
                </div>
              </div>
            </div>
          );
        }) : <div className="glass-card p-10 text-center text-text-muted" data-testid="client-operations-empty-state">No se encontraron clientes con los filtros actuales.</div>}
      </div>
    </div>
  );
};

export default ClientOperationsHub;